/**
 * Single Source of Truth — application-wide constants.
 *
 * Any magic number, threshold, default, or tunable lives here.
 * NEVER hard-code these values inside components or engine modules.
 */

export const APP = {
  NAME: 'Air Đàn Tranh',
  VERSION: '3.0.0',
  TAGLINE: 'Vietnamese zither, played in the air.',
} as const;

// ---------- Hand tracking (MediaPipe Tasks API) ----------

export const HAND = {
  MODEL_URL:
    'https://storage.googleapis.com/mediapipe-models/hand_landmarker/hand_landmarker/float16/1/hand_landmarker.task',
  // WASM is bundled into dist/mp/wasm by vite-plugin-static-copy.
  // Resolved relative to index.html so it works under any base path.
  WASM_CDN: new URL('./mp/wasm', document.baseURI).toString(),

  NUM_HANDS: 2,
  MIN_HAND_DETECTION_CONFIDENCE: 0.5,
  MIN_HAND_PRESENCE_CONFIDENCE: 0.5,
  MIN_TRACKING_CONFIDENCE: 0.5,

  TARGET_FPS: 30,
  FRAME_INTERVAL_MS: 1000 / 30,

  SMOOTHING_WINDOW: 3,
  MISSING_FRAME_TOLERANCE: 4,
} as const;

// ---------- Gesture classifier ----------

export const GESTURE = {
  // Pluck — finger tip crosses a string line downward quickly.
  // Per-hand floor: MediaPipe consistently reports smaller velocity numbers
  // on the LEFT hand than the right for the same motion (likely handedness-
  // asymmetric smoothing inside the model). Calibration 2026-04-23 showed
  // left medium-pluck p50 ≈ 0.30 vs right ≈ 0.30–0.40, but left SLOW pluck
  // p50 = 0.014 while left play-stance noise p50 = 0.007 — so we can drop
  // the LH floor well below the default and still clear noise. RH noise
  // floor p50 = 0.087 keeps its floor higher.
  // Threshold is now checked against the PEAK |velTip| in a short sliding
  // window (see PLUCK_VEL_HISTORY_MS) rather than only the current frame,
  // so a soft stroke that briefly accelerated still fires. Lower floors
  // were safe once the window-peak check absorbs single-frame noise.
  // Single pluck sensitivity for both hands (user directive: "cho 2
  // tay sensitivity bằng nhau"). 0.06 matches the more-sensitive LH
  // baseline — ~8× the play-stance noise floor — so both hands now
  // trigger equally softly. The per-hand constants are kept at the
  // same value for back-compat with persisted settings.
  PLUCK_MIN_VELOCITY: 0.06,
  PLUCK_MIN_VELOCITY_LEFT: 0.06,
  PLUCK_MIN_VELOCITY_RIGHT: 0.06,
  PLUCK_VEL_HISTORY_MS: 100, // keep the last ~3 frames of tip velocity
  PLUCK_REFRACTORY_MS: 80, // min ms between two plucks on same (finger × string)

  // Flick classification via DIP vs PIP velocity (MCP stays near-still
  // during a pluck, so the motion signature is distal joints only). Used
  // by the thumb path (|velTip|/|velIp|). If the ratio is low, the whole
  // finger is translating → GLISSANDO: every crossed string fires.
  // Tuned from calibration 2026-04-21: pluck_thumb |velTip|/|velIp| ratio
  // p50 ≈ 1.35–1.44, p95 ≈ 1.80–2.67. gliss_thumb p95 ≤ 1.47. 1.5 cleanly
  // separates while firing 50%+ of real thumb plucks (2.0 missed most).
  FLICK_DIP_PIP_RATIO: 1.5,
  FLICK_REFRACTORY_MS: 300,

  // Bend — 3 aligned fingers (index/middle/ring) move vertically from anchor.
  BEND_ANCHOR_DEADZONE: 0.015, // normalized units
  BEND_MAX_CENTS: 1200, // default: one octave up / down
  BEND_FULL_RANGE: 0.18, // normalized vertical movement mapped to ±BEND_MAX_CENTS
  // Calibration 2026-04-23: right_bend_still p50 = 0.070, p95 = 0.093.
  // Right_play_stance sits at 0.070. A 0.045 ceiling rejected valid bends
  // outright. 0.090 covers the RH noisy baseline while still rejecting a
  // splayed / spread hand (bend_gentle_vibrato p95 stays under 0.08).
  BEND_ALIGN_THRESHOLD: 0.09, // max |ΔY| between any pair of (idx, mid, ring) tips
  BEND_EXTENDED_THRESHOLD: 0.40, // min curl for ALL 3 fingers to count as cupped → bend

  // Hysteresis — prevents flicker between modes.
  MODE_STICKINESS_MS: 120,
} as const;

// ---------- Audio ----------

export const AUDIO = {
  DEFAULT_STRING_COUNT: 17,
  MAX_POLYPHONY: 24,

  BASE_A4_HZ: 440,
  DEFAULT_SAMPLE_RATE: 48000,
  LATENCY_HINT: 'interactive' as AudioContextLatencyCategory,

  ENVELOPE: {
    ATTACK_MS: 15,
    DECAY_MS: 500,
    SUSTAIN: 0.7,
    RELEASE_MS: 8000,
  },

  VOLUME: { MIN: 0, MAX: 1, DEFAULT: 0.75 },

  BEND: {
    // Short enough to track vibrato (~5–8 Hz) at 30 fps tracking without
    // low-passing it out; long enough to avoid zipper noise.
    GLIDE_MS: 12,
    // Release glide — when bend ends, ramp pitch back to neutral over
    // this window instead of snapping. Much longer than the tracking
    // glide so the "snapback" is audible but not jarring.
    RELEASE_MS: 150,
  },
} as const;

// ---------- Scale / tuning ----------

export const SCALE = {
  DEFAULT_ROOT: 'C',
  DEFAULT_OCTAVE: 4,
  DEFAULT_FAMILY: 'pentatonic-major',

  // Vietnamese 7-EDO (equal division of octave into 7 steps)
  VIETNAMESE_7EDO_STEP_CENTS: 1200 / 7,
} as const;

// ---------- Performance ----------

export const PERF = {
  QUALITY_LEVELS: ['low', 'medium', 'high'] as const,
  DEFAULT_QUALITY: 'high' as const,
  FPS_SAMPLE_WINDOW: 60,
  MEMORY_WARN_MB: 300,
} as const;

// ---------- UI theme (CSS vars + tokens) ----------

export const UI = {
  COLORS: {
    BG: '#0a0a0f',
    SURFACE: '#13131a',
    SURFACE_HI: '#1c1c26',
    FG: '#f5f5f7',
    MUTED: 'rgba(245,245,247,0.6)',
    ACCENT: '#ffd24a',
    ACCENT_HI: '#ffde7d',
    /** Perfect-5th string colour. Red alongside the yellow root gives
     *  the player two visible anchors per scale — root (yellow) + its
     *  strongest interval partner (red). */
    FIFTH: '#ef4444',
    LEFT_HAND: '#7dd3fc', // sky-300
    RIGHT_HAND: '#fca5a5', // red-300
    ERROR: '#ff6b6b',
    SUCCESS: '#4ade80',
  },
  RADIUS: { SM: 6, MD: 12, LG: 20 },
  SPACING: { XS: 4, SM: 8, MD: 16, LG: 24, XL: 40 },
} as const;

export type QualityLevel = (typeof PERF.QUALITY_LEVELS)[number];
